/**
 * "Change…" on a customer's Billed to (docs/features/credit-control/
 * 20_billed_to_update_spec.md): which customer pays for this customer's CIS
 * orders. A deliberate action like Put on stop, with its own reason, not part
 * of the page's one Save.
 *
 * - "Pays for itself", or "Billed to another customer" with a customer search
 *   (the customer spine, active customers only, debounced, never this
 *   customer);
 * - a required reason;
 * - what does not change: orders already placed keep their payer, and a
 *   customer with its own billing account keeps it, unused.
 *
 * The row that opens it owns the call; a refusal comes back bound here: a
 * field error under the input it names, anything else (a 409 or 404) inline
 * in the backend's words.
 */
import { useEffect, useState } from "react";
import { Check } from "lucide-react";
import type * as API from "@shared-core/data/api-types";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  CustomerCreditView,
  SetBilledToInput,
} from "@shared-core/data/credit-control-types";
import { Alert, AlertDescription } from "../../primitives/alert";
import { Button } from "../../primitives/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../primitives/dialog";
import { Input } from "../../primitives/input";
import { Label } from "../../primitives/label";
import { RadioGroup, RadioGroupItem } from "../../primitives/radio-group";
import { Textarea } from "../../primitives/textarea";

/** Why the last save was refused, bound to where it is shown. */
export interface BilledToError {
  /** Under the customer search (`payer_customer_code`). */
  payerMessage: string | null;
  /** Under the reason (`reason`). */
  reasonMessage: string | null;
  /** Anything that names no input: a 409 or a 404, in the backend's words. */
  message: string | null;
}

interface PayerChoice {
  customerCode: string;
  name: string;
}

type Mode = "itself" | "customer";

const PAYER_MESSAGE_ID = "billed-to-payer-error";
const REASON_MESSAGE_ID = "billed-to-reason-error";
const SEARCH_DEBOUNCE_MS = 300;

/** The customer search: the spine's active customers matching `query`, never this customer. */
function usePayerSearch(
  enabled: boolean,
  query: string,
  credit: Pick<CustomerCreditView, "customerCode" | "customerId">,
) {
  const adapter = useDataAdapter();
  const [results, setResults] = useState<API.CisCustomer[]>([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    const timer = setTimeout(
      () => {
        adapter
          .listCisCustomers({
            q: query.trim() || undefined,
            pageSize: 20,
            isActive: true,
          })
          .then(
            (page) => {
              if (cancelled) return;
              setResults(
                page.items.filter(
                  (c) =>
                    c.customer_code !== credit.customerCode &&
                    c.tharstern_customer_code !== credit.customerId,
                ),
              );
              setLoading(false);
            },
            () => {
              if (cancelled) return;
              setFailed(true);
              setLoading(false);
            },
          );
      },
      query ? SEARCH_DEBOUNCE_MS : 0,
    );
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [adapter, enabled, query, retry, credit.customerCode, credit.customerId]);

  return { results, loading, failed, retry: () => setRetry((n) => n + 1) };
}

export function BilledToDialog({
  open,
  onOpenChange,
  credit,
  error,
  saving,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  credit: CustomerCreditView;
  /** The last refused save, bound to its inputs or shown inline. */
  error: BilledToError | null;
  saving: boolean;
  onConfirm: (input: SetBilledToInput) => void;
}) {
  const current: PayerChoice | null = credit.payer
    ? {
        customerCode: credit.payer.customerCode,
        name: credit.payer.customerName,
      }
    : null;
  const [mode, setMode] = useState<Mode>(current ? "customer" : "itself");
  const [payer, setPayer] = useState<PayerChoice | null>(current);
  const [query, setQuery] = useState("");
  const [reason, setReason] = useState("");
  const search = usePayerSearch(open && mode === "customer", query, credit);

  const reset = () => {
    setMode(current ? "customer" : "itself");
    setPayer(current);
    setQuery("");
    setReason("");
  };

  const chosen = mode === "itself" ? null : payer;
  const unchanged =
    (chosen?.customerCode ?? null) === (current?.customerCode ?? null);
  const canSave =
    reason.trim() !== "" &&
    (mode === "itself" || payer !== null) &&
    !unchanged &&
    !saving;
  const fieldErrorCount =
    (error?.payerMessage ? 1 : 0) + (error?.reasonMessage ? 1 : 0);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) reset();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Who pays for {credit.customerName}'s orders</DialogTitle>
          <DialogDescription>
            Orders already placed keep the payer they were created with.
          </DialogDescription>
        </DialogHeader>
        {error && (fieldErrorCount > 0 || error.message) && (
          <Alert variant="destructive" role="alert">
            <AlertDescription>
              {fieldErrorCount > 0
                ? `Not saved: ${fieldErrorCount} ${fieldErrorCount === 1 ? "problem" : "problems"} marked below.`
                : error.message}
            </AlertDescription>
          </Alert>
        )}
        <RadioGroup
          value={mode}
          onValueChange={(value) => setMode(value as Mode)}
          aria-label="Billed to"
          className="space-y-1"
        >
          <div className="flex items-center gap-2">
            <RadioGroupItem value="itself" id="billed-to-itself" />
            <Label htmlFor="billed-to-itself" className="font-normal">
              Pays for itself
            </Label>
          </div>
          <div className="flex items-center gap-2">
            <RadioGroupItem value="customer" id="billed-to-customer" />
            <Label htmlFor="billed-to-customer" className="font-normal">
              Billed to another customer
            </Label>
          </div>
        </RadioGroup>
        {mode === "customer" && (
          <div className="space-y-2">
            <Input
              aria-label="Search customers by name or code"
              placeholder="Search by name or code…"
              value={query}
              aria-invalid={error?.payerMessage ? true : undefined}
              aria-describedby={
                error?.payerMessage ? PAYER_MESSAGE_ID : undefined
              }
              onChange={(event) => setQuery(event.target.value)}
            />
            {error?.payerMessage && (
              <p id={PAYER_MESSAGE_ID} className="text-sm text-red-600">
                {error.payerMessage}
              </p>
            )}
            <div
              role="group"
              aria-label="Customers"
              aria-live="polite"
              className="max-h-56 space-y-1 overflow-y-auto"
            >
              {search.loading ? (
                <p className="text-sm text-muted-foreground">
                  Loading customers…
                </p>
              ) : search.failed ? (
                <p role="alert" className="text-sm">
                  Customers could not be loaded.{" "}
                  <Button size="sm" variant="outline" onClick={search.retry}>
                    Try again
                  </Button>
                </p>
              ) : search.results.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No customers found.
                </p>
              ) : (
                search.results.map((result) => {
                  const selected = payer?.customerCode === result.customer_code;
                  return (
                    <Button
                      key={result.customer_code}
                      type="button"
                      variant="ghost"
                      className="h-auto min-h-9 w-full justify-between gap-3 whitespace-normal text-left"
                      aria-pressed={selected}
                      onClick={() =>
                        setPayer({
                          customerCode: result.customer_code,
                          name: result.name,
                        })
                      }
                    >
                      <span className="min-w-0 break-words">{result.name}</span>
                      <span className="flex shrink-0 items-center gap-2 text-muted-foreground">
                        {result.customer_code}
                        {selected && (
                          <Check className="size-4" aria-hidden="true" />
                        )}
                      </span>
                    </Button>
                  );
                })
              )}
            </div>
            {payer && (
              <p className="text-sm">
                Billed to{" "}
                <span className="font-medium">
                  {payer.name} ({payer.customerCode})
                </span>
              </p>
            )}
          </div>
        )}
        <div className="space-y-1.5">
          <Label htmlFor="billed-to-reason">Reason (required)</Label>
          <Textarea
            id="billed-to-reason"
            rows={2}
            value={reason}
            aria-invalid={error?.reasonMessage ? true : undefined}
            aria-describedby={
              error?.reasonMessage ? REASON_MESSAGE_ID : undefined
            }
            onChange={(e) => setReason(e.target.value)}
          />
          {error?.reasonMessage && (
            <p id={REASON_MESSAGE_ID} className="text-sm text-red-600">
              {error.reasonMessage}
            </p>
          )}
        </div>
        {credit.hasOwnBillingAccount && (
          <p className="text-sm text-muted-foreground">
            Its own payment settings are kept, unused, while it is billed to
            someone else.
          </p>
        )}
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={!canSave}
            onClick={() =>
              onConfirm({
                payerCustomerCode: chosen?.customerCode ?? null,
                reason: reason.trim(),
              })
            }
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
